import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import moment from 'moment';
import { IMaskInput } from 'react-imask';
import { NumericFormat } from 'react-number-format';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import SelectEventDateForm from './SelectEventDateForm';
import { FormControl, InputLabel, Select, MenuItem, Autocomplete, Grid, Box, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import TextField from '@mui/material/TextField';
import 'react-phone-number-input/style.css';

import { message } from 'antd';
import { isPhoneValid } from '../utils/phoneNumbersValidator';

import MaterialPhoneInput from '../utils/MaterialPhoneInput'

// Mantenemos los componentes personalizados existentes
const TextMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, values, handleChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="#00-000-0000"
      definitions={{
        '#': /[8]/,
      }}
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

TextMaskCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const NumericFormatCustom = React.forwardRef(
  function NumericFormatCustom(props, ref) {
    const { onChange, values, handleChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
        prefix="RD$"
      />
    );
  },
);

NumericFormatCustom.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

// Componente para mostrar mensajes de error con estilo inline
const ErrorMessageStyled = ({ children }) => (
  <Typography
    sx={{
      color: 'error.main',
      fontSize: '0.75rem',
      mt: 0.5,
      mb: 0.5,
      fontWeight: 'medium'
    }}
  >
    {children}
  </Typography>
);

const safeParseFloat = (value, defaultValue = 0) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

const formatCurrency = (value) => {
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const EventFormXV = forwardRef((props, ref) => {
  const {
    handleFormValues, formValues, nextStepHandler, reservationStep, clients,
    requestClients, dressList, getDressListHandler, eventsScheduledXV, dressReservations
  } = props;
  const formRef = useRef(null);
  const [eventDate, setEventDate] = useState(null);
  const [shortDressList, setShortDressList] = useState([]);
  const [longDressList, setLongDressList] = useState([]);
  const [availableShortDresses, setAvailableShortDresses] = useState([]);
  const [availableLongDresses, setAvailableLongDresses] = useState([]);
  const [clientType, setClientType] = useState('new');
  const [clientsList, setClientsList] = useState([]);

  // Usar theme y media queries para responsividad
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Mantenemos todas las funciones y useEffects existentes
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      if (formRef.current) {
        formRef.current.submitForm()
      }
    }
  }));

  const handleEventDate = (date) => {
    setEventDate(date)
  };

  const handleClientTypeChange = (event) => {
    setClientType(event.target.value);
  };

  const filterAvailableDresses = (dressList) => {
    return dressList.filter(dress => {
      return !dressReservations.some(reservation => {
        const reservationStart = new Date(reservation.eventDateStart);
        const reservationEnd = new Date(reservation.eventDateEnd);
        const eventStart = new Date(eventDate.start);
        const eventEnd = new Date(eventDate.end);
        return (
          reservation.dressID === dress.dressID &&
          ((eventStart >= reservationStart && eventStart <= reservationEnd) ||
            (eventEnd >= reservationStart && eventEnd <= reservationEnd) ||
            (eventStart <= reservationStart && eventEnd >= reservationEnd))
        );
      });
    });
  };

  const handleChangeShortDress = (dress) => {
    let shortDress = [];
    for (let i = 0; i < dress.length; i++) {
      if (dress[i].dressSize === 'short') {
        shortDress.push(dress[i])
      }
    }
    setShortDressList([...shortDress])
  };

  const handleChangeLongDress = (dress) => {
    let longDress = [];
    for (let i = 0; i < dress.length; i++) {
      if (dress[i].dressSize === 'long' && dress[i].dressType === 'xv') {
        longDress.push(dress[i])
      }
    }
    setLongDressList([...longDress])
  };

  const handleReservationInf = async (values) => {
    const formValues = {
      client: {
        clientName: values.fatherName,
        phoneNumber: values.fatherPhone,
        cellPhoneNumber: values.fatherCellphone,
        clientEmail: values.fatherEmail,
        address: values.address1,
        clientID: values.clientID
      },
      XV: {
        XVName: values.XVName,
        XVCellphoneNumber: values.XVCellphone,
        XVEmail: values.XVEmail,
        birthday: values.birthday
      },
      event: {
        eventDateStart: moment(eventDate.start).format('YYYY-MM-DD HH:mm:ss'),
        eventDateEnd: moment(eventDate.end).format('YYYY-MM-DD HH:mm:ss'),
        eventType: values.eventType,
        eventLocation: Object.keys(values.eventPlace).length > 0 ? values.eventPlace : 'Sesión en estudio.',
        eventTitle: `Quinceaños de ${values.XVName}`,
        eventDescription: `${values.comboName} - ${values.eventPlace ? values.eventPlace : 'Sesión en estudio.'}`,
        status: 'pendiente',
        paymentStatus: 'pendiente',
      },
      reservation: {
        comboName: values.comboName,
        comboPrice: values.comboPrice,
        shortDress: values.shortDress,
        shortDressID: values.shortDressID,
        longDress: values.longDress,
        longDressID: values.longDressID,
        deposit: 2000,
        paymentStatus: 'pendiente',
        referralSource: values.referralSource,
        discount: values.discount,
        notes: values.notes
      },
      dressAgenda: {
        eventDateStart: moment(eventDate.start).format('YYYY-MM-DD HH:mm:ss'),
        eventDateEnd: moment(eventDate.end).format('YYYY-MM-DD HH:mm:ss'),
        shortDressID: values.shortDressID,
        longDressID: values.longDressID,
        status: 'pendiente',
      },
      reservationCashFlow: {
        paymentMethod: 'efectivo',
        amount: 2000,
        description: `Reserva de combo (${values.comboName}) - ${Object.keys(values.eventPlace).length > 0 ? values.eventPlace : 'Sesión en estudio.'}`,
        ServiceID: 24,
        flowType: 'ingreso',
      },
      reservationServices: {
        serviceQty: 1,
        serviceID: 24,
        discount: values.discount,
        status: 'pendiente',
        servicePrice: values.comboPrice
      }
    }
    console.log(formValues);
    handleFormValues(formValues);
    nextStepHandler();
  };

  const handleFormSubmit = async (values) => {
    console.log(values.longDress);
    return await handleReservationInf(values);
  };

  useEffect(() => {
    window.onbeforeunload = () => {
      window.scrollTo(0, 0);
    };
  }, []);

  useEffect(() => {
    //console.log(clients, 'Clients');
    if (clients && clients.length > 0) {
      const options = clients
        .filter(client => client.clientName !== 'Cliente al contado') // Filtrar cliente "Contado"
        .map((client) => ({
          label: `${client.clientName} - ${client.cellphoneNumber}`,
          value: client
        }));
      setClientsList([...options]);
    }
    else {
      requestClients();
    }
  }, []);

  useEffect(() => {
    if (clients && clients.length > 0) {
      const options = clients.map((client) => ({
        label: `${client.clientName} - ${client.cellphoneNumber}`,
        value: client
      }));
      setClientsList([...options]);
    }
  }, [clientType]);

  useEffect(() => {
    if (dressList.length === 0) {
      getDressListHandler();
    }
    else {
      handleChangeShortDress(dressList);
      handleChangeLongDress(dressList);
    }
  }, []);

  useEffect(() => {
    if (dressList.length === 0) {
      getDressListHandler();
    }
    else {
      handleChangeShortDress(dressList);
      handleChangeLongDress(dressList);
    }
  }, [dressList]);

  useEffect(() => {
    if (Object.keys(formValues).length > 0) {
      console.log(formValues.client.clientEmail, 'Client name');
    }
  }, [formValues]);

  useEffect(() => {
    if (eventDate) {
      setAvailableShortDresses(filterAvailableDresses(shortDressList));
      setAvailableLongDresses(filterAvailableDresses(longDressList));
    }
  }, [eventDate]);

  useEffect(() => {
    if (formValues?.client?.clientID) {
      setClientType('existing');
    }
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: { xs: '100%', sm: 600, md: 800, lg: 950 },
        mx: 'auto',
        px: { xs: 1, sm: 2, md: 3, lg: 4 },
        py: { xs: 2, sm: 3, md: 4 },
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          mb: { xs: 2, sm: 3, md: 4 },
          textAlign: 'center',
          width: '100%'
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: '#f759ab',
            fontWeight: 'bold',
            fontSize: { xs: '1.3rem', sm: '1.7rem', md: '2.1rem', lg: '2.25rem' },
            lineHeight: 1.2
          }}
        >
          Formulario de reservación de XV años
        </Typography>
      </Box>

      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, mx: 'auto' }}>
        <Formik
          initialValues={{
            fatherName: Object.keys(formValues).length > 0 ? formValues.client.clientName : '',
            fatherPhone: Object.keys(formValues).length > 0 ? formValues.client.phoneNumber : '',
            fatherCellphone: Object.keys(formValues).length > 0 ? formValues.client.cellPhoneNumber : '',
            fatherEmail: Object.keys(formValues).length > 0 ? formValues.client.clientEmail : '',
            address1: Object.keys(formValues).length > 0 ? formValues.client.address : '',
            XVName: Object.keys(formValues).length > 0 ? formValues.XV.XVName : '',
            birthday: Object.keys(formValues).length > 0 ? formValues.XV.birthday : '',
            XVCellphone: Object.keys(formValues).length > 0 ? formValues.XV.cellphoneNumber : '',
            XVEmail: Object.keys(formValues).length > 0 ? formValues.XV.XVEmail : '',
            eventDate: eventDate,
            shortDress: Object.keys(formValues).length > 0 ? formValues.reservation.shortDress : '',
            shortDressID: Object.keys(formValues).length > 0 ? formValues.reservation.shortDressID : '',
            longDress: Object.keys(formValues).length > 0 ? formValues.reservation.longDress : '',
            longDressID: Object.keys(formValues).length > 0 ? formValues.reservation.longDressID : '',
            comboName: Object.keys(formValues).length > 0 ? formValues.reservation.comboName : '',
            comboPrice: Object.keys(formValues).length > 0 ? formValues.reservation.comboPrice : '',
            reservationPayment: Object.keys(formValues).length > 0 ? formValues.reservation.deposit : '',
            referralSource: Object.keys(formValues).length > 0 ? formValues.reservation.referralSource : '',
            discount: Object.keys(formValues).length > 0 ? formValues.reservation.discount : 0,
            eventType: Object.keys(formValues).length > 0 ? formValues.event.eventType : null,
            eventPlace: Object.keys(formValues).length > 0 ? formValues.event.eventLocation : '',
            clientID: Object.keys(formValues).length > 0 ? formValues.client.clientID : null,
            notes: Object.keys(formValues).length > 0 ? formValues.notes : ''
          }}

          onSubmit={(values, { setSubmitting }) => {
            handleFormSubmit(values)
            setSubmitting(false)
          }}
          innerRef={formRef}
          validate={values => {
            const errors = {};
            if (!values.fatherName) {
              errors.fatherName = '*El nombre es obligatorio';
            }
            if (!values.fatherEmail) {
              errors.fatherEmail = '*Requerido';
            } else if (
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.fatherEmail)
            ) {
              errors.fatherEmail = 'Correo electrónico invalido';
            }
            if (!values.XVName) {
              errors.XVName = '*El nombre es obligatorio'
            }
            if (!values.XVEmail) {
              errors.XVEmail = '*Requerido'
            }
            if (!values.fatherCellphone) {
              errors.fatherCellphone = '*Requerido'
            }
            if (values.fatherCellphone?.length < 10) {
              errors.fatherCellphone = 'Por favor introduce un número de celular valido'
            }
            if (values.fatherCellphone === '') {
              errors.fatherCellphone = '*Requerido'
            }
            if (values.comboPrice === 0) {
              errors.comboPrice = '*Requerido'
            }
            if (values.comboPrice < 6000) {
              errors.comboPrice = 'Introduce un precio valido.'
            }
            if (!eventDate) {
              errors.eventDate = 'Seleccione la fecha del evento'
            }
            if (Object.keys(errors).length > 0) {
              errors.isSubmitting = 'Debes llenar todos los campos requeridos en el formulario.'
            }
            if (values.eventType === null || values.eventType.length === 0) {
              errors.eventType = 'Debes seleccionar el tipo de evento para poder agendar.'
            }
            if (values.shortDress === null || values.shortDress.length === 0 || values.shortDress === '') {
              errors.shortDress = 'Debes seleccionar un vestido corto para la quinceañera.'
            }
            if (values.longDress === null || values.longDress.length === 0 || values.longDress === '') {
              errors.longDress = 'Debes seleccionar un vestido largo para la quinceañera.'
            }
            if (values.birthday === '' || values.birthday === null) {
              errors.birthday = '*Requerido'
            }
            return errors;
          }}
        >
          {({
            isSubmitting,
            errors,
            touched,
            setFieldValue,
            handleSubmit,
            handleChange,
            values
          }) => (
            <Form>
              {/* Sección de información del padre o tutor */}
              <Paper
                elevation={2}
                sx={{
                  mb: 3,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  border: '2px solid #ff85c0',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-16px',
                    left: '16px',
                    px: 2,
                    py: 0.5,
                    backgroundColor: '#ffadd2',
                    borderRadius: '16px',
                    zIndex: 1
                  }}
                >
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                    Información del padre o tutor
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Tipo de Cliente"
                        value={clientType}
                        onChange={handleClientTypeChange}
                        variant="outlined"
                      >
                        <MenuItem value="new">Nuevo Cliente</MenuItem>
                        <MenuItem value="existing" disabled={clients.length === 0}>
                          {clients.length > 0 ? "Cliente Registrado" : "No hay clientes registrados"}
                        </MenuItem>
                      </TextField>
                    </Grid>

                    {clientType === 'new' ? (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Field name="fatherName">
                            {({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                id="outlined-basic"
                                label="Nombre"
                                variant="outlined"
                                color={errors.fatherName ? 'error' : !errors.fatherName && touched.fatherName ? 'success' : ''}
                                focused={touched.fatherName}
                                placeholder={errors.fatherName ? '*Requerido' : ''}
                                initialValues={formValues.length > 0 && formValues.client.clientName}
                                error={touched.fatherName && Boolean(errors.fatherName)}
                                autoComplete='clientName'
                              />
                            )}
                          </Field>
                          <ErrorMessage name='fatherName' component={ErrorMessageStyled} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Field type="email" name="fatherEmail">
                            {({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                id="outlined-basic"
                                label="Email"
                                variant="outlined"
                                color={errors.fatherEmail ? 'error' : !errors.fatherEmail && touched.fatherEmail ? 'success' : ''}
                                focused={touched.fatherEmail}
                                placeholder={errors.fatherEmail ? '*Requerido' : ''}
                                error={touched.fatherEmail && Boolean(errors.fatherEmail)}
                                autoComplete='clientEmail'
                              />
                            )}
                          </Field>
                          <ErrorMessage name='fatherEmail' component={ErrorMessageStyled} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Field name='fatherPhone'>
                            {({ field, form, meta }) => (
                              <MaterialPhoneInput
                                label="Número de teléfono"
                                name={field.name}
                                value={field.value}
                                // El onChange de PhoneInput devuelve el valor directamente,
                                // por lo que usamos setFieldValue para actualizar Formik.
                                onChange={(phone) => {
                                  form.setFieldValue(field.name, phone);
                                }}
                                // Pasamos el estado de error y el texto de ayuda desde Formik
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                                color={meta.error ? 'error' : (!meta.error && meta.touched ? 'success' : undefined)}
                                labelFontSize={18}
                                defaultCountry="do" // Puedes pasar cualquier otra prop de PhoneInput aquí
                                Autocomplete='clientPhone'
                              />
                            )}
                          </Field>
                          <ErrorMessage name='fatherPhone' component={ErrorMessageStyled} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Field name='fatherCellphone'>
                            {({ field, form, meta }) => (
                              <MaterialPhoneInput
                                label="Número de celular"
                                name={field.name}
                                value={field.value}
                                // El onChange de PhoneInput devuelve el valor directamente,
                                // por lo que usamos setFieldValue para actualizar Formik.
                                onChange={(phone) => {
                                  form.setFieldValue(field.name, phone);
                                }}
                                // Pasamos el estado de error y el texto de ayuda desde Formik
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                                color={meta.error ? 'error' : (!meta.error && meta.touched ? 'success' : undefined)}
                                labelFontSize={18}
                                defaultCountry="do" // Puedes pasar cualquier otra prop de PhoneInput aquí
                                Autocomplete='clientCellphone'
                              />
                            )}
                          </Field>
                          <ErrorMessage name='fatherCellphone' component={ErrorMessageStyled} />
                        </Grid>

                        <Grid item xs={12}>
                          <Field name="address1">
                            {({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                id="outlined-basic"
                                label="Dirección de residencia"
                                variant="outlined"
                              />
                            )}
                          </Field>
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Autocomplete
                          freeSolo
                          options={clientsList}
                          getOptionLabel={(option) => option.label}
                          value={
                            clientsList.find(option => option.value.clientName === values.fatherName)
                            || values.fatherName || clientsList[0]?.clientName
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Nombre o celular" variant="outlined" />
                          )}
                          onChange={(event, value) => {
                            if (value && typeof value === 'object') {
                              setFieldValue('fatherName', value.value.clientName);
                              setFieldValue('fatherPhone', value.value.phoneNumber);
                              setFieldValue('fatherCellphone', value.value.cellphoneNumber);
                              setFieldValue('fatherEmail', value.value.clientEmail);
                              setFieldValue('address1', value.value.address);
                              setFieldValue('clientID', value.value.clientID);
                            } else {
                              setFieldValue('fatherName', value || '');
                              setFieldValue('fatherPhone', '');
                              setFieldValue('fatherCellphone', '');
                              setFieldValue('fatherEmail', '');
                              setFieldValue('address1', '');
                              setFieldValue('clientID', null);
                            }
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Paper>

              {/* Sección de información de la quinceañera */}
              <Paper
                elevation={2}
                sx={{
                  mb: 3,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  border: '2px solid #ff85c0',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-16px',
                    left: '16px',
                    px: 2,
                    py: 0.5,
                    backgroundColor: '#ffadd2',
                    borderRadius: '16px',
                    zIndex: 1
                  }}
                >
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                    Información de la quinceañera
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Field name="XVName">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            id="outlined-basic"
                            label="Nombre de la Quinceañera"
                            variant="outlined"
                            color={errors.XVName ? 'error' : !errors.XVName && touched.XVName ? 'success' : ''}
                            focused={touched.XVName}
                            placeholder={errors.XVName ? '*Requerido' : ''}
                            initialValues={formValues.length > 0 && formValues.XV.XVName}
                            autoComplete='XVName'
                            error={touched.XVName && Boolean(errors.XVName)}
                          />
                        )}
                      </Field>
                      <ErrorMessage name='XVName' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="birthday" type="date">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            id="outlined-basic"
                            label="Fecha de Cumpleaños"
                            variant="outlined"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            color={errors.birthday ? 'error' : !errors.birthday && touched.birthday ? 'success' : ''}
                            error={touched.birthday && Boolean(errors.birthday)}
                          />
                        )}
                      </Field>
                      <ErrorMessage name='birthday' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name='XVCellphone'>
                        {({ field, form, meta }) => (
                          <MaterialPhoneInput
                            label="Número de teléfono"
                            name={field.name}
                            value={field.value}
                            autoComplete='XVCellphone'
                            onChange={(phone) => {
                              form.setFieldValue(field.name, phone);
                            }}
                            // Pasamos el estado de error y el texto de ayuda desde Formik
                            error={meta.touched && Boolean(meta.error)}
                            helperText={meta.touched && meta.error}
                            color={meta.error ? 'error' : (!meta.error && meta.touched ? 'success' : undefined)}
                            labelFontSize={18}
                            defaultCountry="do" // Puedes pasar cualquier otra prop de PhoneInput aquí
                          />
                        )}
                      </Field>
                      <ErrorMessage name='XVCellphone' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="XVEmail" type="email">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            id="outlined-basic"
                            label="Email de la Quinceañera"
                            variant="outlined"
                            initialValues={formValues.length > 0 && formValues.XV.XVEmail}
                            autoComplete='XVEmail'
                          />
                        )}
                      </Field>
                      <ErrorMessage name='XVEmail' component={ErrorMessageStyled} />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* Sección de información del evento */}
              <Paper
                elevation={2}
                sx={{
                  mb: 3,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  border: '2px solid #ff85c0',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-16px',
                    left: '16px',
                    px: 2,
                    py: 0.5,
                    backgroundColor: '#ffadd2',
                    borderRadius: '16px',
                    zIndex: 1
                  }}
                >
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                    Información del evento
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={values.eventType === null ? 12 : 6}>
                      <Field name="eventType">
                        {({ field, form }) => (
                          <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-autowidth-label">Tipo de evento</InputLabel>
                            <Select
                              labelId="simple-select-autowidth-label"
                              id="simple-select-autowidth"
                              value={field.value || ''}
                              onChange={(event) => {
                                values.eventType = event.target.value
                                form.setFieldValue(field.name, event.target.value);
                              }}
                              label="Tipo de evento"
                              error={touched.eventType && Boolean(errors.eventType)}
                            >
                              <MenuItem key='outside' value='outside'>Evento en exterior</MenuItem>
                              <MenuItem key='inside' value='inside'>Evento en estudio</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Field>
                      <ErrorMessage name='eventType' component={ErrorMessageStyled} />
                    </Grid>

                    {values.eventType === 'inside' && (
                      <Grid item xs={12} sm={6}>
                        <Field name="eventDate" type="date">
                          {({ field }) => (
                            <Box sx={{ height: '100%' }}>
                              <SelectEventDateForm
                                {...field}
                                labelName='Fecha del evento (Estudio)'
                                handleEventDate={handleEventDate}
                                eventsScheduledXV={eventsScheduledXV}
                                typeOfEvent='inside'
                                eventDate={Object.keys(formValues).length > 0 ? formValues.event.eventDateStart : eventDate}
                              />
                            </Box>
                          )}
                        </Field>
                        <ErrorMessage name='eventDate' component={ErrorMessageStyled} />
                      </Grid>
                    )}

                    {values.eventType === 'outside' && (
                      <Grid item xs={12} sm={6}>
                        <Field name="eventDate" type="date">
                          {({ field }) => (
                            <Box sx={{ height: '100%' }}>
                              <SelectEventDateForm
                                {...field}
                                labelName='Fecha del evento en exterior'
                                handleEventDate={handleEventDate}
                                eventsScheduledXV={eventsScheduledXV}
                                eventDate={Object.keys(formValues).length > 0 ? formValues.event.eventDateStart : eventDate}
                                typeOfEvent='outside'
                              />
                            </Box>
                          )}
                        </Field>
                        <ErrorMessage name='eventDate' component={ErrorMessageStyled} />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                      <Field name="shortDress">
                        {({ field, form }) => (
                          <FormControl fullWidth>
                            <Autocomplete
                              disablePortal
                              options={availableShortDresses}
                              getOptionLabel={(option) => `${option.code} - ${option.dressName}`}
                              value={availableShortDresses.find(option => `${option.code} - ${option.dressName}` === field.value) || null}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  id="shortDress"
                                  name="shortDress"
                                  label="Vestido corto"
                                  value={field.value || ''}
                                  error={form.touched.shortDress && Boolean(form.errors.shortDress)}
                                  margin="normal"
                                />
                              )}
                              onChange={(event, value) => {
                                form.setFieldValue(field.name, value ? `${value.code} - ${value.dressName}` : '');
                                form.setFieldValue('shortDressID', value ? value.dressID : '');
                              }}
                            />
                          </FormControl>
                        )}
                      </Field>
                      <ErrorMessage name='shortDress' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="longDress">
                        {({ field, form }) => (
                          <FormControl fullWidth>
                            <Autocomplete
                              disablePortal
                              options={availableLongDresses}
                              getOptionLabel={(option) => `${option.code} - ${option.dressName}`}
                              value={longDressList.find(option => `${option.code} - ${option.dressName}` === field.value) || null}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  id="longDress"
                                  name="longDress"
                                  label="Vestido largo"
                                  value={field.value || ''}
                                  error={form.touched.longDress && Boolean(form.errors.longDress)}
                                  margin="normal"
                                />
                              )}
                              onChange={(event, value) => {
                                form.setFieldValue(field.name, value ? `${value.code} - ${value.dressName}` : '');
                                form.setFieldValue('longDressID', value ? value.dressID : '');
                              }}
                            />
                          </FormControl>
                        )}
                      </Field>
                      <ErrorMessage name='longDress' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12}>
                      <Field name="eventPlace">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            id="outlined-basic"
                            label="Lugar del evento"
                            variant="outlined"
                            disabled={values.eventType === 'inside'}
                            value={values.eventType === 'inside' ? 'Sesión en estudio.' : field.value}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="referralSource">
                        {({ field, form }) => (
                          <FormControl>
                            <InputLabel id="demo-simple-select-autowidth-label">¿Cómo escucho de nosotros?</InputLabel>
                            <Select
                              labelId="simple-select-autowidth-label"
                              id="simple-select-autowidth"
                              value={field.value || 'fuente-desconocida'}
                              onChange={(event) => {
                                values.referralSource = event.target.value
                                form.setFieldValue(field.name, event.target.value);
                              }}
                              label="¿Cómo escucho de nosotros?"
                              fullWidth
                            >
                              <MenuItem key='instagram' value='instagram'>Instagram</MenuItem>
                              <MenuItem key='facebook' value='facebook'>Facebook</MenuItem>
                              <MenuItem key='google' value='google'>Google</MenuItem>
                              <MenuItem key='Referido' value='Referido'>Referido</MenuItem>
                              <MenuItem key='other' value='other'>Otro</MenuItem>
                              <MenuItem key='fuente-desconocida' value='fuente-desconocida'>Fuente desconocida</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="comboName">
                        {({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            id="outlined-basic"
                            label="Nombre del Combo"
                            variant="outlined"
                          />
                        )}
                      </Field>
                      <ErrorMessage name='comboName' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="comboPrice">
                        {({ field }) => (
                          <TextField
                            fullWidth
                            label="Precio del combo"
                            value={values.comboPrice}
                            onChange={handleChange}
                            name="comboPrice"
                            id="formatted-NumberFormat-input"
                            slotProps={{
                              input: {
                                inputComponent: NumericFormatCustom,
                                values: values,
                                handleChange: handleChange,
                              },
                            }}
                            variant="outlined"
                            error={touched.comboPrice && Boolean(errors.comboPrice)}
                          />
                        )}
                      </Field>
                      <ErrorMessage name='comboPrice' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="discount">
                        {({ field }) => (
                          <TextField
                            {...field}
                            id="outlined-basic"
                            label="Descuento (%)"
                            variant="outlined"
                            value={values.discount}
                            type="number"
                            inputProps={{
                              min: 0,
                              max: 35,
                              step: 1
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Solo permitir números enteros positivos menores a 36
                              if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) < 36)) {
                                handleChange(e);
                              }
                            }}
                            helperText="Máximo 35%"
                            fullWidth
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="totalPrice">
                        {({ field }) => (
                          <TextField {...field} id="outlined-basic" label="Precio total" variant="outlined" disabled
                            value={`RD$ ${formatCurrency(safeParseFloat(values.comboPrice || 0) * ((100 - safeParseFloat(values.discount || 0)) / 100))}`}
                            fullWidth
                          />
                        )}
                      </Field>
                      <ErrorMessage name='totalPrice' component={ErrorMessageStyled} />
                    </Grid>

                    <Grid item xs={12}>
                      <Field name="notes">
                        {({ field }) => (
                          <TextField
                            {...field}
                            id="outlined-basic"
                            label="Notas (opcional)"
                            placeholder='Escribe tus notas u observaciones'
                            variant="outlined"
                            multiline
                            rows={4}
                            fullWidth
                            value={values.notes}
                            onChange={handleChange}
                          />
                        )}
                      </Field>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* Mensaje de error general */}
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <ErrorMessage name='isSubmitting' component={ErrorMessageStyled} />
              </Box>

              {/* Botón de siguiente */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: reservationStep === 0 ? 'flex-end' : 'space-evenly',
                  width: '100%',
                  mt: 3
                }}
              >
                <Button
                  variant='contained'
                  onClick={(e) => nextStepHandler(e)}
                  sx={{
                    backgroundColor: '#eb2f96',
                    '&:hover': {
                      backgroundColor: '#c41d7f',
                    },
                    py: 1.2,
                    px: 3,
                    borderRadius: 2,
                    fontWeight: 'bold'
                  }}
                >
                  Siguiente
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
});

export default EventFormXV;